<template>
  <v-app :style="appStyles">
    <AppSideNav v-if="showShell" />
    <AppHeader v-if="showShell" />
    <v-snackbar
      :model-value="snackbarIsOpen"
      @update:model-value="onSnackbarModelUpdate"
      :timeout="4000"
      location="bottom"
      :color="snackbarColor"
      rounded="pill"
      class="appSnackbar"
    >
      {{ snackbarText }}
      <template #actions>
        <v-btn color="white" variant="text" @click="closeSnackbar">Close</v-btn>
      </template>
    </v-snackbar>
    <v-main :class="mainClass">
      <div
        v-if="showShell && actingCompanyName"
        class="actingBanner"
        data-testid="acting-company-banner"
      >
        <span>Viewing as {{ actingCompanyName }}</span>
        <v-btn size="small" variant="text" color="white" @click="clearActingCompany">Return home</v-btn>
      </div>
      <router-view />
    </v-main>
  </v-app>
</template>

<script>
import { mapGetters, mapMutations, mapState } from 'vuex'
import AppSideNav from './components/navigation/AppSideNav.vue'
import AppHeader from './components/navigation/AppHeader.vue'
import API from './api/myco'
import PlatformAPI from './api/platform'
import AuthAPI from './api/auth'
import { parseCompanyProperties } from './utils/companySettings'
import { messageForApiError } from './utils/apiErrorMessage'
import { setDocumentTitle } from './utils/documentTitle'
import { startNotificationPolling, stopNotificationPolling } from './utils/notificationPolling'
import './styles/app-shell.scss'

export default {
  name: 'App',
  components: { AppSideNav, AppHeader },
  computed: {
    ...mapState(['snackbarIsOpen', 'snackbarColor', 'snackbarText', 'user', 'company']),
    ...mapGetters(['themeColor']),
    showShell() {
      return Boolean(this.user && this.$route.meta.requiresAuth)
    },
    appStyles() {
      return { '--app-theme-color': this.themeColor }
    },
    mainClass() {
      return this.showShell ? 'mainWithNav' : ''
    },
    actingCompanyName() {
      const acting = Number(this.user?.actingCompanyId || 0)
      const home = Number(this.user?.homeCompanyId || 0)
      if (!acting || acting === home) return ''
      return this.company?.name || `Company ${acting}`
    },
  },
  watch: {
    company: {
      immediate: true,
      handler(value) {
        setDocumentTitle(value?.name)
      },
    },
    user: {
      immediate: true,
      handler(user) {
        if (user?.companyId) this.loadCompany(user.companyId)
        if (user) {
          this.syncBrowserTimezone()
          startNotificationPolling(this.$store, this.$router)
        } else {
          stopNotificationPolling()
        }
      },
    },
  },
  beforeUnmount() {
    stopNotificationPolling()
  },
  methods: {
    ...mapMutations(['closeSnackbar']),
    onSnackbarModelUpdate(value) {
      if (!value) this.closeSnackbar()
    },
    async loadCompany(companyId) {
      try {
        const { data } = await API.company.get(companyId)
        if (typeof data?.properties === 'string') {
          data.properties = parseCompanyProperties(data.properties)
        }
        this.$store.commit('setCompany', data)
      } catch {
        /* company settings optional for shell */
      }
    },
    syncBrowserTimezone() {
      try {
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
        if (timezone) {
          // Best-effort: expired sessions return 401 — never leave an unhandled rejection.
          AuthAPI.setTimezone(timezone).catch(() => {})
        }
      } catch {
        /* timezone sync is best-effort */
      }
    },
    async clearActingCompany() {
      try {
        const { data } = await PlatformAPI.setActingCompany(null)
        if (data?.user) this.$store.commit('setUser', data.user)
        if (data?.company) {
          const company = { ...data.company }
          if (typeof company.properties === 'string') {
            company.properties = parseCompanyProperties(company.properties)
          }
          this.$store.commit('setCompany', company)
        }
        await this.$store.dispatch('clearTenantCaches')
        await this.$router.push('/dashboard')
      } catch (err) {
        this.$store.commit('setSnackbar', { type: 'error', message: messageForApiError(err, 'Could not return to home company.') })
      }
    },
  },
}
</script>

<style>
* {
  --var-app-cyan: #1fe9d3;
  --var-app-violet: #2a2971;
  font-family: 'Montserrat', sans-serif !important;
}
html {
  overflow-x: clip;
  max-width: 100%;
}
body {
  max-width: 100%;
}
.v-application {
  overflow-x: clip;
  max-width: 100%;
}
.mainWithNav {
  min-height: 100vh;
}
.appSnackbar {
  z-index: 2500;
}
.actingBanner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 16px;
  background: #b45309;
  color: #fff;
  font-size: 14px;
}
</style>
